import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, MessageCircle } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { MaskLine } from "@/components/Reveal";

const HERO_IMG = "https://images.pexels.com/photos/16571735/pexels-photo-16571735.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1100&w=1600";

const Hero = () => {
  const { t, openBooking, openWhatsApp } = useSite();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [1, 0.4]);

  return (
    <section id="top" ref={ref} className="relative min-h-screen w-full overflow-hidden flex flex-col justify-center" data-testid="hero">
      <motion.div style={{ y, scale }} className="absolute inset-0 z-0">
        <img src={HERO_IMG} alt="Modern regenerative medicine clinic environment" className="w-full h-full object-cover" />
      </motion.div>
      <motion.div style={{ opacity: overlayOpacity }} className="absolute inset-0 z-10 bg-gradient-to-b from-navy/70 via-navy/45 to-navy/80" />
      <div className="absolute inset-0 z-10 bg-gradient-to-r from-navy/50 to-transparent" />

      <div className="relative z-20 max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 w-full pt-28 pb-20">
        <motion.p
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5, duration: 1 }}
          className="text-champagne/90 text-xs sm:text-sm tracking-[0.3em] uppercase font-sans mb-6">
          {t.hero.eyebrow}
        </motion.p>

        <h1 className="font-serif text-white leading-[0.95] tracking-tight text-6xl sm:text-7xl lg:text-[8.5rem]">
          <MaskLine delay={0.35}>{t.hero.line1}</MaskLine>
          <MaskLine delay={0.5} className="text-champagne italic font-light">{t.hero.line2}</MaskLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.9 }}
          className="mt-8 text-white/90 font-sans text-lg sm:text-xl max-w-xl">
          {t.hero.secondary}
        </motion.p>
        <motion.p
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.05, duration: 0.9 }}
          className="mt-5 text-white/70 font-serif italic text-xl sm:text-2xl max-w-2xl leading-snug">
          “{t.hero.supporting}”
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.25, duration: 0.9 }}
          className="mt-10 flex flex-col sm:flex-row gap-4">
          <button onClick={() => openBooking()}
            className="group inline-flex items-center justify-center gap-2 rounded-full bg-white text-navy font-medium px-8 py-4 hover:bg-champagne transition-colors duration-300"
            data-testid="hero-book">
            {t.hero.ctaPrimary}
            <ArrowDown size={16} className="-rotate-90 group-hover:translate-x-1 transition-transform" />
          </button>
          <button onClick={() => openWhatsApp()}
            className="inline-flex items-center justify-center gap-2 rounded-full border border-white/40 text-white font-medium px-8 py-4 hover:bg-white/10 transition-colors duration-300"
            data-testid="hero-whatsapp">
            <MessageCircle size={18} />
            {t.hero.ctaSecondary}
          </button>
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.5, duration: 1 }}
          className="mt-12 text-champagne text-sm sm:text-base tracking-[0.2em] uppercase font-sans">
          {t.hero.slogan}
        </motion.p>
      </div>

      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.8, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex flex-col items-center gap-2 text-white/60">
        <span className="text-[10px] tracking-[0.2em] uppercase font-sans">{t.hero.scroll}</span>
        <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 1.8 }}>
          <ArrowDown size={16} />
        </motion.div>
      </motion.div>
    </section>
  );
};

export default Hero;
